<template>
    <Form as="" :validation-schema="schema" v-slot="{ meta, handleSubmit }">
        <bs5-base-modal :title="$t('users.addTitle')" :visible="visible" @close="closeModal">
            <template v-slot:default>
                <form novalidate @submit.prevent="handleSubmit($event, addUser)">
                    <bs5-form-field id="addUserFullName" name="fullName" icon="bi-person" autocomplete="name"
                        :label="$t('users.fullName')" v-model="newUser.fullName" />
                    <bs5-form-field id="addUserEmail" name="email" type="email" icon="bi-envelope" autocomplete="off"
                        :label="$t('users.email')" v-model="newUser.email" />
                    <bs5-form-field id="addUserRole" name="role" :label="$t('users.role')" :options="roleChoices"
                        v-model="newUser.role" />
                    <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
                </form>
            </template>

            <template v-slot:footer>
                <bs5-cancel-button @click="closeModal" />
                <bs5-save-button :loading="saving" :disabled="!meta.valid" @click="handleSubmit($event, addUser)">
                    <i class="bi bi-plus-lg"></i>{{ $t("common.add") }}
                </bs5-save-button>
            </template>
        </bs5-base-modal>
    </Form>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent, ref } from "vue";
import { Form } from "vee-validate";
import * as yup from "yup";
import userService from "@/services/user.service";
import { User } from "@/models/user.model";
import { UserRole } from "@/enums/user-role.enum";
import { showToast } from "@/utils/toast";
import { requiredChoice, requiredEmail, requiredText } from "@/utils/validation";

export default defineComponent({
    name: "AddUserModal",
    components: { Form },
    props: {
        visible: { type: Boolean, required: true },
    },
    emits: ["close", "user-added"],
    data() {
        return {
            newUser: ref<User>({
                fullName: "",
                email: "",
                role: UserRole.Client,
            }),
            roleOptions: [
                { value: UserRole.SuperAdmin, labelKey: "roles.SuperAdmin" },
                { value: UserRole.Distributor, labelKey: "roles.Distributor" },
                { value: UserRole.Client, labelKey: "roles.Client" },
            ],
            saving: ref(false),
        };
    },
    computed: {
        schema() {
            return yup.object({
                fullName: requiredText(256),
                email: requiredEmail(),
                role: requiredChoice(),
            });
        },
        roleChoices(): { value: number; label: string }[] {
            return this.roleOptions.map(role => ({ value: role.value, label: t(role.labelKey) }));
        },
    },
    watch: {
        visible(isVisible: boolean) {
            if (isVisible) {
                this.newUser = { fullName: "", email: "", role: UserRole.Client };
            }
        },
    },
    methods: {
        async addUser() {
            try {
                this.saving = true;
                await userService.createUser(this.newUser);
                this.$emit("user-added");
                showToast(t("users.added"), "success");
                this.closeModal();
            } catch (error) {
                showToast(t("users.addFailed"), "error");
                console.error("Error adding user:", error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
});
</script>
